import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, Snackbar, Typography } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { makeStyles } from "@material-ui/core/styles";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import GpsFixedIcon from "@material-ui/icons/GpsFixed";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { TRACKER_STATUS } from "./getTrackingStatusInfo";
import OperationalGisMap from "./OperationalGisMap";
import FavoriteBorderIcon from "@material-ui/icons/FavoriteBorder";
import DirectionsBusIcon from "@material-ui/icons/DirectionsBus";

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 24,
    overflowX: "hidden",
    maxHeight: "calc(100vh - 96px)",

    [theme.breakpoints.down("sm")]: {
      padding: 16,
      maxHeight: "calc(100vh - 48px)",
    },
  },

  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 18,
  },

  headerIcon: {
    width: 44,
    height: 44,
    minWidth: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
    backgroundColor: "rgba(142, 36, 170, 0.12)",
    color: "#8e24aa",
  },

  title: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.2,
  },

  subtitle: {
    fontSize: 13,
    color: "rgba(0, 0, 0, 0.6)",
    marginTop: 4,
  },

  mapContainer: {
    height: "calc(100vh - 470px)",
    minHeight: 320,
    maxHeight: 460,
    marginBottom: 20,

    [theme.breakpoints.down("sm")]: {
      height: 320,
      minHeight: 320,
      maxHeight: 320,
    },
  },

  historyCard: {
    marginTop: -6,
    marginBottom: 20,
    padding: "12px 14px",
    borderRadius: 10,
    border: "1px solid rgba(142, 36, 170, 0.22)",
    backgroundColor: "rgba(142, 36, 170, 0.045)",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      marginBottom: 14,
    },
  },

  historyHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 8,

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: 6,
    },
  },

  historyHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: 6,
    },
  },

  historyPlaybackButton: {
    minWidth: 0,
    padding: "3px 9px",
    borderRadius: 999,
    border: "1px solid rgba(142, 36, 170, 0.22)",
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    color: "#8e24aa",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.4,
    textTransform: "none",

    "&:hover": {
      backgroundColor: "rgba(142, 36, 170, 0.08)",
    },
  },

  historyTitleBlock: {
    minWidth: 0,
  },

  historyTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#4a145a",
    lineHeight: 1.25,
  },

  historySubtitle: {
    marginTop: 2,
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.62)",
    lineHeight: 1.3,
  },

  historyCount: {
    flexShrink: 0,
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    color: "#8e24aa",
    backgroundColor: "rgba(142, 36, 170, 0.1)",
    whiteSpace: "nowrap",

    [theme.breakpoints.down("sm")]: {
      alignSelf: "flex-start",
    },
  },

  historyRange: {
    marginBottom: 10,
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.66)",
  },

  historyTimeline: {
    position: "relative",
    height: 22,
    margin: "2px 8px 0",

    [theme.breakpoints.down("sm")]: {
      margin: "2px 4px 0",
    },
  },

  historyRail: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 10,
    height: 2,
    borderRadius: 999,
    backgroundColor: "rgba(142, 36, 170, 0.24)",
  },

  historyPoint: {
    position: "absolute",
    top: 5,
    width: 12,
    height: 12,
    borderRadius: "50%",
    backgroundColor: "#8e24aa",
    border: "2px solid #ffffff",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.22)",
    transform: "translateX(-50%)",
  },

  historyPointStart: {
    backgroundColor: "#6a1b9a",
  },

  historyPointEnd: {
    top: 4,
    width: 14,
    height: 14,
    backgroundColor: "#d71920",
  },

  historyPointSelected: {
    boxShadow: "0 0 0 3px rgba(142, 36, 170, 0.18), 0 2px 7px rgba(0, 0, 0, 0.24)",
    transform: "translateX(-50%) scale(1.16)",
    zIndex: 2,
  },

  historyPointButton: {
    appearance: "none",
    padding: 0,
    cursor: "pointer",

    "&:focus-visible": {
      outline: "none",
      boxShadow: "0 0 0 3px rgba(142, 36, 170, 0.28), 0 2px 7px rgba(0, 0, 0, 0.24)",
    },
  },

  historyPointDetail: {
    marginTop: 10,
    padding: "9px 10px",
    borderRadius: 8,
    border: "1px solid rgba(142, 36, 170, 0.16)",
    backgroundColor: "rgba(255, 255, 255, 0.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,

    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: 4,
    },
  },

  historyPointDetailTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#4a145a",
  },

  historyPointDetailCoordinates: {
    fontSize: 12,
    color: "rgba(0, 0, 0, 0.74)",
    fontFamily: "monospace",
    wordBreak: "break-word",
  },

  historyLabels: {
    display: "flex",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 2,
    fontSize: 11,
    color: "rgba(0, 0, 0, 0.62)",
  },

  historyLabel: {
    whiteSpace: "nowrap",
  },

  positionCard: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1fr",
    borderRadius: 6,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
    backgroundColor: "#ffffff",
    overflow: "hidden",
    marginTop: "35px",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },

  contextCard: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    marginTop: 14,
    borderRadius: 6,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    backgroundColor: "#ffffff",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },

  contextItem: {
    display: "flex",
    alignItems: "center",
    padding: "14px 18px",
    borderRight: "1px solid rgba(0, 0, 0, 0.08)",

    "&:last-child": {
      borderRight: "none",
    },

    [theme.breakpoints.down("sm")]: {
      borderRight: "none",
      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",

      "&:last-child": {
        borderBottom: "none",
      },
    },
  },

  contextIcon: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
  },

  contextR100Icon: {
    backgroundColor: "rgba(215, 25, 32, 0.12)",
    color: "#d71920",
  },

  contextBusIcon: {
    backgroundColor: "rgba(0, 159, 227, 0.12)",
  },

  contextTitle: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.25,
  },

  contextValue: {
    fontSize: 13,
    color: "rgba(0, 0, 0, 0.72)",
    marginTop: 3,
    lineHeight: 1.3,
  },

  positionContent: {
    width: "100%",
  },

  positionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },

  copyButton: {
    minWidth: "auto",
    padding: "2px 6px",
    fontSize: 12,
    textTransform: "none",
    whiteSpace: "nowrap",
  },

  coordinatesValue: {
    fontSize: 13,
    color: "rgba(0, 0, 0, 0.78)",
    marginTop: 4,
    lineHeight: 1.35,
    fontFamily: "monospace",
  },

  positionItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: "16px 20px",
    borderRight: "1px solid rgba(0, 0, 0, 0.08)",

    "&:last-child": {
      borderRight: "none",
    },

    [theme.breakpoints.down("sm")]: {
      borderRight: "none",
      borderBottom: "1px solid rgba(0, 0, 0, 0.08)",

      "&:last-child": {
        borderBottom: "none",
      },
    },
  },

  positionIcon: {
    color: "#8e24aa",
    marginRight: 12,
    marginTop: 2,
    fontSize: 20,
  },

  positionLabel: {
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.25,
  },

  positionValue: {
    fontSize: 13,
    color: "rgba(0, 0, 0, 0.72)",
    marginTop: 3,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },

  actions: {
    padding: "12px 24px 18px",

    [theme.breakpoints.down("sm")]: {
      padding: "8px 16px 16px",
    },
  },
}));

const TRACE_PLAYBACK_INTERVAL_MS = 900;

const isSameTracePoint = (a, b) => a?.traceIndex === b?.traceIndex && a?.position_at === b?.position_at;

const getTracePayload = (trackerOperationalGis) => trackerOperationalGis?.r100_position_trace?.rootElement ?? trackerOperationalGis?.r100_position_trace;

const getOperationalMapPosition = (trackerStatus, trackerOperationalGis) => {
  const opening = trackerOperationalGis?.opening_position;
  const tracePayload = getTracePayload(trackerOperationalGis);
  const lastValid = tracePayload?.last_valid;

  if (trackerStatus === TRACKER_STATUS.NO_SIGNAL) {
    return lastValid ?? (opening?.valid ? opening : null);
  }

  if (trackerStatus === TRACKER_STATUS.EXTRACTED) {
    return lastValid ?? (opening?.valid ? opening : null);
  }

  return null;
};

const hasTimezoneInfo = (dateText) => /Z$|[+-]\d{2}:?\d{2}$/.test(dateText);

const formatMadridDate = (date) => {
  const formatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

const normalizeTraceDate = (value) => {
  if (!value) {
    return null;
  }

  const dateText = String(value).trim();

  if (!dateText) {
    return null;
  }

  const normalizedDateText = hasTimezoneInfo(dateText) ? dateText : `${dateText}Z`;
  const parsedDate = new Date(normalizedDateText);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatMadridTime = (value) => {
  const parsedDate = value instanceof Date ? value : normalizeTraceDate(value);

  if (!parsedDate) {
    return null;
  }

  return parsedDate.toLocaleTimeString("es-ES", {
    timeZone: "Europe/Madrid",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getTracePointsWithDate = (tracePoints) => {
  if (!Array.isArray(tracePoints)) {
    return [];
  }

  return tracePoints
    .map((point, index) => ({
      ...point,
      traceIndex: index,
      parsedDate: normalizeTraceDate(point?.position_at),
    }))
    .filter((point) => point.parsedDate);
};

const getTimelineDisplayPoints = (points, maxPoints = 8) => {
  if (!Array.isArray(points) || points.length <= maxPoints) {
    return points || [];
  }

  const lastIndex = points.length - 1;
  const step = lastIndex / (maxPoints - 1);
  const selectedIndexes = new Set();

  for (let index = 0; index < maxPoints; index += 1) {
    selectedIndexes.add(Math.round(index * step));
  }

  selectedIndexes.add(0);
  selectedIndexes.add(lastIndex);

  return Array.from(selectedIndexes)
    .sort((a, b) => a - b)
    .map((index) => points[index])
    .filter(Boolean);
};

const getTraceTimelineSummary = (tracePoints) => {
  const pointsWithDate = getTracePointsWithDate(tracePoints);

  if (pointsWithDate.length === 0) {
    return {
      pointsWithDate: [],
      displayPoints: [],
      startTime: null,
      endTime: null,
    };
  }

  const sortedPoints = [...pointsWithDate].sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());
  const firstPoint = sortedPoints[0];
  const lastPoint = sortedPoints[sortedPoints.length - 1];

  return {
    pointsWithDate: sortedPoints,
    displayPoints: getTimelineDisplayPoints(sortedPoints, 8),
    startTime: formatMadridTime(firstPoint.parsedDate),
    endTime: formatMadridTime(lastPoint.parsedDate),
  };
};

const getOperationalPositionDate = (operationalPosition, trackerOperationalGis) => {
  const rawDate = operationalPosition?.position_at || trackerOperationalGis?.opening_position?.position_at;

  if (!rawDate) {
    return "-";
  }

  const dateText = String(rawDate);
  const normalizedDateText = hasTimezoneInfo(dateText) ? dateText : `${dateText}Z`;
  const parsedDate = new Date(normalizedDateText);

  return Number.isNaN(parsedDate.getTime()) ? "-" : formatMadridDate(parsedDate);
};

const getOpeningAddress = (trackerOperationalGis, t) =>
  trackerOperationalGis?.opening_position?.address || trackerOperationalGis?.address || t("devices.tracking.config.status.operational_map.position.address_unavailable", "No disponible");

const formatCoordinate = (coordinate) => {
  const value = Number(coordinate);

  return Number.isNaN(value) ? "-" : value.toFixed(6);
};

const getOperationalCoordinatesText = (operationalPosition) => {
  const latitude = formatCoordinate(operationalPosition?.latitude);
  const longitude = formatCoordinate(operationalPosition?.longitude);

  return latitude === "-" || longitude === "-" ? "-" : `${latitude}, ${longitude}`;
};

const getTracePointCoordinatesText = (point) => {
  const latitude = formatCoordinate(point?.latitude);
  const longitude = formatCoordinate(point?.longitude);

  return latitude === "-" || longitude === "-" ? "-" : `${latitude}, ${longitude}`;
};

const canCopyValue = (value) => Boolean(value && value !== "-");

const copyToClipboard = (value) => {
  if (!canCopyValue(value)) {
    return Promise.resolve(false);
  }

  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard.writeText(value).then(() => true);
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  return Promise.resolve(copied);
};

const OperationalEventGisOverlay = ({ open, onClose, trackerStatus, trackerOperationalGis, busPosition, busStatusInfo, device, onRefresh, t }) => {
  const classes = useStyles();
  const [copyMessage, setCopyMessage] = useState("");
  const [liveDevice, setLiveDevice] = useState(device);
  const [selectedTracePoint, setSelectedTracePoint] = useState(null);
  const [isTracePlaybackRunning, setIsTracePlaybackRunning] = useState(false);
  const [googleMapsApi, setGoogleMapsApi] = useState(null);
  const [reverseGeocodedLastR100Address, setReverseGeocodedLastR100Address] = useState({
    key: "",
    value: "",
    status: "idle",
  });

  const isRefreshingRef = useRef(false);
  const reverseGeocodingRequestKeyRef = useRef("");

  useEffect(() => {
    if (open) {
      setLiveDevice(device);
    }
  }, [open, device]);

  useEffect(() => {
    if (!open || !onRefresh) {
      return undefined;
    }

    const refresh = async () => {
      if (document.hidden || isRefreshingRef.current) {
        return;
      }

      isRefreshingRef.current = true;

      try {
        const refreshedDevice = await onRefresh();

        if (refreshedDevice?.id) {
          setLiveDevice((prev) => ({
            ...prev,
            ...refreshedDevice,
            tracker_operational_gis: refreshedDevice.tracker_operational_gis ?? prev?.tracker_operational_gis,
            tracker_position: refreshedDevice.tracker_position !== undefined ? refreshedDevice.tracker_position : prev?.tracker_position,
            tracker_assignments: refreshedDevice.tracker_assignments ?? prev?.tracker_assignments,
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        isRefreshingRef.current = false;
      }
    };

    refresh();

    const intervalId = setInterval(refresh, 60000);

    return () => clearInterval(intervalId);
  }, [open, onRefresh]);

  const isExtracted = trackerStatus === TRACKER_STATUS.EXTRACTED;
  const isNoSignal = trackerStatus === TRACKER_STATUS.NO_SIGNAL;
  const effectiveDevice = liveDevice || device;
  const effectiveTrackerOperationalGis = liveDevice?.tracker_operational_gis || trackerOperationalGis;
  const effectiveBusPosition = liveDevice?.tracker_position !== undefined ? liveDevice.tracker_position : busPosition;
  const operationalPosition = getOperationalMapPosition(trackerStatus, effectiveTrackerOperationalGis);
  const lastR100Latitude = Number(operationalPosition?.latitude);
  const lastR100Longitude = Number(operationalPosition?.longitude);

  const hasValidLastR100Coordinates = Number.isFinite(lastR100Latitude) && Number.isFinite(lastR100Longitude);

  const lastR100AddressKey = hasValidLastR100Coordinates ? `${lastR100Latitude.toFixed(6)},${lastR100Longitude.toFixed(6)}` : "";
  const tracePayload = getTracePayload(effectiveTrackerOperationalGis);
  const trace = Array.isArray(tracePayload?.trace) ? tracePayload.trace : [];
  const traceTimelineSummary = useMemo(() => getTraceTimelineSummary(trace), [trace]);
  const shouldShowTraceTimeline = isNoSignal && trace.length > 1 && traceTimelineSummary.pointsWithDate.length > 0;

  useEffect(() => {
    if (!open || !shouldShowTraceTimeline || traceTimelineSummary.displayPoints.length === 0) {
      setSelectedTracePoint(null);
      return;
    }

    setSelectedTracePoint((previousSelectedTracePoint) => {
      if (previousSelectedTracePoint) {
        const stillAvailablePoint = traceTimelineSummary.displayPoints.find((point) => isSameTracePoint(point, previousSelectedTracePoint));

        if (stillAvailablePoint) {
          return stillAvailablePoint;
        }
      }

      return traceTimelineSummary.displayPoints[traceTimelineSummary.displayPoints.length - 1];
    });
  }, [open, shouldShowTraceTimeline, traceTimelineSummary.displayPoints]);

  const selectedTracePointIndex = traceTimelineSummary.displayPoints.findIndex((point) => isSameTracePoint(point, selectedTracePoint));

  const handleToggleTracePlayback = () => {
    if (!shouldShowTraceTimeline || traceTimelineSummary.displayPoints.length === 0) {
      return;
    }

    if (isTracePlaybackRunning) {
      setIsTracePlaybackRunning(false);
      return;
    }

    const lastIndex = traceTimelineSummary.displayPoints.length - 1;

    if (selectedTracePointIndex >= lastIndex) {
      setSelectedTracePoint(traceTimelineSummary.displayPoints[0]);
    }

    setIsTracePlaybackRunning(true);
  };

  useEffect(() => {
    if (!isTracePlaybackRunning || !shouldShowTraceTimeline || traceTimelineSummary.displayPoints.length === 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setSelectedTracePoint((currentPoint) => {
        const currentIndex = traceTimelineSummary.displayPoints.findIndex((point) => isSameTracePoint(point, currentPoint));

        if (currentIndex < 0) {
          return traceTimelineSummary.displayPoints[0];
        }

        const nextIndex = currentIndex + 1;

        if (nextIndex >= traceTimelineSummary.displayPoints.length) {
          setIsTracePlaybackRunning(false);
          return currentPoint;
        }

        return traceTimelineSummary.displayPoints[nextIndex];
      });
    }, TRACE_PLAYBACK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isTracePlaybackRunning, shouldShowTraceTimeline, traceTimelineSummary.displayPoints]);

  useEffect(() => {
    if (!open || !shouldShowTraceTimeline) {
      setIsTracePlaybackRunning(false);
    }
  }, [open, shouldShowTraceTimeline]);

  useEffect(() => {
    if (!open || !isNoSignal || !hasValidLastR100Coordinates || !lastR100AddressKey) {
      reverseGeocodingRequestKeyRef.current = "";

      setReverseGeocodedLastR100Address({
        key: "",
        value: "",
        status: "idle",
      });

      return undefined;
    }

    const Geocoder = googleMapsApi?.Geocoder || window.google?.maps?.Geocoder;

    if (!Geocoder) {
      return undefined;
    }

    if (reverseGeocodingRequestKeyRef.current === lastR100AddressKey) {
      return undefined;
    }

    reverseGeocodingRequestKeyRef.current = lastR100AddressKey;

    setReverseGeocodedLastR100Address({
      key: lastR100AddressKey,
      value: "",
      status: "loading",
    });

    const geocoder = new Geocoder();

    geocoder.geocode(
      {
        location: {
          lat: lastR100Latitude,
          lng: lastR100Longitude,
        },
      },
      (results, status) => {
        if (reverseGeocodingRequestKeyRef.current !== lastR100AddressKey) {
          return;
        }

        if ((status === "OK" || status === googleMapsApi?.GeocoderStatus?.OK) && results?.[0]?.formatted_address) {
          setReverseGeocodedLastR100Address({
            key: lastR100AddressKey,
            value: results[0].formatted_address,
            status: "resolved",
          });
          return;
        }

        reverseGeocodingRequestKeyRef.current = "";

        setReverseGeocodedLastR100Address({
          key: lastR100AddressKey,
          value: "",
          status: "failed",
        });
      }
    );

    return undefined;
  }, [open, isNoSignal, hasValidLastR100Coordinates, lastR100AddressKey, lastR100Latitude, lastR100Longitude, googleMapsApi]);

  const unavailableAddressText = t("devices.tracking.config.status.operational_map.position.address_unavailable", "No disponible");

  const lastR100AddressUnavailableText = t("devices.tracking.config.status.operational_map.last_r100_address_unavailable", "Dirección del último registro R100 no disponible");

  const lastR100AddressLoadingText = t("devices.tracking.config.status.operational_map.last_r100_address_loading", "Resolviendo dirección…");

  const openingAddressText = operationalPosition ? getOpeningAddress(effectiveTrackerOperationalGis, t) : "";

  const lastR100AddressText =
    reverseGeocodedLastR100Address.status === "resolved"
      ? reverseGeocodedLastR100Address.value
      : reverseGeocodedLastR100Address.status === "loading"
      ? lastR100AddressLoadingText
      : lastR100AddressUnavailableText;

  const addressText = operationalPosition ? (isNoSignal ? lastR100AddressText : openingAddressText) : "";

  const addressCopyText = isNoSignal ? (reverseGeocodedLastR100Address.status === "resolved" ? reverseGeocodedLastR100Address.value : "") : openingAddressText;

  const canCopyAddress = isNoSignal ? Boolean(addressCopyText) : addressText !== unavailableAddressText;

  const coordinatesText = operationalPosition ? getOperationalCoordinatesText(operationalPosition) : "-";

  const associatedBus = effectiveTrackerOperationalGis?.associated_bus || effectiveDevice?.tracker_assignments?.bus;

  const associatedBusNumber =
    associatedBus?.bus_number || associatedBus?.busNumber || associatedBus?.number || effectiveDevice?.bus || effectiveDevice?.bus_number || effectiveDevice?.busNumber || "-";

  const associatedBusLicensePlate =
    associatedBus?.license_plate ||
    associatedBus?.licensePlate ||
    effectiveDevice?.license_plate ||
    effectiveDevice?.plate ||
    effectiveDevice?.registration ||
    t("devices.tracking.config.status.operational_map.context.unavailable", "No disponible");

  const handleCopy = (value, message) => {
    copyToClipboard(value).then((copied) => {
      if (copied) {
        setCopyMessage(message);
      }
    });
  };

  const titleKey = isExtracted ? "devices.tracking.config.status.operational_map.empty.extracted.title" : "devices.tracking.config.status.operational_map.empty.no_signal.title";

  const descriptionKey = isExtracted ? "devices.tracking.config.status.operational_map.empty.extracted.description" : "devices.tracking.config.status.operational_map.empty.no_signal.description";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        style: {
          width: "92vw",
          maxWidth: 1280,
        },
      }}
    >
      <DialogContent className={classes.content}>
        {operationalPosition ? (
          <>
            <div className={classes.header}>
              <div className={classes.headerIcon}>
                <LocationOnIcon />
              </div>

              <div>
                <Typography className={classes.title}>{t("devices.tracking.config.status.operational_map.position.title", "Ubicación del evento")}</Typography>
                <Typography className={classes.subtitle}>{t("devices.tracking.config.status.operational_map.position.description", "Última ubicación registrada durante el evento.")}</Typography>
              </div>
            </div>

            <div className={classes.mapContainer}>
              <OperationalGisMap
                position={operationalPosition}
                trace={trace}
                showTrace={isNoSignal && trace.length > 1}
                traceDisplayPoints={shouldShowTraceTimeline ? traceTimelineSummary.displayPoints : []}
                selectedTracePoint={shouldShowTraceTimeline ? selectedTracePoint : null}
                busPosition={effectiveBusPosition}
                busStatusInfo={busStatusInfo}
                onMapApiReady={({ maps }) => setGoogleMapsApi(maps)}
                t={t}
              />
            </div>

            {shouldShowTraceTimeline && (
              <div className={classes.historyCard}>
                <div className={classes.historyHeader}>
                  <div className={classes.historyTitleBlock}>
                    <Typography className={classes.historyTitle}>{t("devices.tracking.config.status.operational_map.history.title", "Histórico R100")}</Typography>

                    <Typography className={classes.historySubtitle}>
                      {t("devices.tracking.config.status.operational_map.history.subtitle", "Recorrido registrado antes de la pérdida de señal · Última posición válida en rojo")}
                    </Typography>
                  </div>

                  <div className={classes.historyHeaderActions}>
                    <div className={classes.historyCount}>
                      {t("devices.tracking.config.status.operational_map.history.count", "{{count}} posiciones", {
                        count: trace.length,
                      })}
                    </div>

                    <Button size="small" className={classes.historyPlaybackButton} onClick={handleToggleTracePlayback} disabled={traceTimelineSummary.displayPoints.length < 2}>
                      {isTracePlaybackRunning
                        ? t("devices.tracking.config.status.operational_map.history.pause", "Pausa")
                        : t("devices.tracking.config.status.operational_map.history.play", "Reproducir")}
                    </Button>
                  </div>
                </div>

                <Typography className={classes.historyRange}>
                  {t("devices.tracking.config.status.operational_map.history.range", "De {{start}} a {{end}}", {
                    start: traceTimelineSummary.startTime || t("devices.tracking.config.status.operational_map.history.no_date", "hora no disponible"),
                    end: traceTimelineSummary.endTime || t("devices.tracking.config.status.operational_map.history.no_date", "hora no disponible"),
                  })}
                </Typography>

                <div className={classes.historyTimeline}>
                  <div className={classes.historyRail} />

                  {traceTimelineSummary.displayPoints.map((point, index) => {
                    const displayPointCount = traceTimelineSummary.displayPoints.length;
                    const isFirst = index === 0;
                    const isLast = index === displayPointCount - 1;
                    const left = displayPointCount === 1 ? 0 : (index / (displayPointCount - 1)) * 100;
                    const pointTime = formatMadridTime(point.parsedDate);

                    const isSelected = isSameTracePoint(selectedTracePoint, point);

                    return (
                      <button
                        type="button"
                        key={`${point.traceIndex}-${point.position_at || index}`}
                        className={[
                          classes.historyPoint,
                          classes.historyPointButton,
                          isFirst ? classes.historyPointStart : "",
                          isLast ? classes.historyPointEnd : "",
                          isSelected ? classes.historyPointSelected : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{ left: `${left}%` }}
                        title={pointTime || ""}
                        onClick={() => {
                          setIsTracePlaybackRunning(false);
                          setSelectedTracePoint(point);
                        }}
                        aria-label={t("devices.tracking.config.status.operational_map.history.select_point", "Seleccionar punto histórico de las {{time}}", {
                          time: pointTime || t("devices.tracking.config.status.operational_map.history.no_date", "hora no disponible"),
                        })}
                      />
                    );
                  })}
                </div>

                <div className={classes.historyLabels}>
                  <span className={classes.historyLabel}>{traceTimelineSummary.startTime}</span>
                  <span className={classes.historyLabel}>{traceTimelineSummary.endTime}</span>
                </div>

                {selectedTracePoint && (
                  <div className={classes.historyPointDetail}>
                    <Typography className={classes.historyPointDetailTitle}>
                      {t("devices.tracking.config.status.operational_map.history.point_detail_title", "Punto seleccionado")} ·{" "}
                      {selectedTracePoint.parsedDate ? formatMadridTime(selectedTracePoint.parsedDate) : t("devices.tracking.config.status.operational_map.history.no_date", "hora no disponible")}
                    </Typography>

                    <Typography className={classes.historyPointDetailCoordinates}>
                      {t("devices.tracking.config.status.operational_map.history.point_coordinates", "Coordenadas")}: {getTracePointCoordinatesText(selectedTracePoint)}
                    </Typography>
                  </div>
                )}
              </div>
            )}

            <div className={classes.positionCard}>
              <div className={classes.positionItem}>
                <LocationOnIcon className={classes.positionIcon} />

                <div className={classes.positionContent}>
                  <div className={classes.positionHeader}>
                    <Typography className={classes.positionLabel}>
                      {isNoSignal
                        ? t("devices.tracking.config.status.operational_map.last_r100_address_title", "Dirección aproximada del último registro R100")
                        : t("devices.tracking.config.status.operational_map.position.opening_address", "Dirección aproximada de apertura del evento")}
                    </Typography>

                    {canCopyAddress && (
                      <Button
                        size="small"
                        color="primary"
                        className={classes.copyButton}
                        startIcon={<FileCopyIcon fontSize="small" />}
                        onClick={() => handleCopy(addressCopyText, t("devices.tracking.config.status.operational_map.position.address_copied", "Dirección copiada"))}
                      >
                        {t("devices.tracking.config.status.operational_map.position.copy", "Copiar")}
                      </Button>
                    )}
                  </div>

                  <Typography className={classes.positionValue}>{addressText}</Typography>
                </div>
              </div>

              <div className={classes.positionItem}>
                <GpsFixedIcon className={classes.positionIcon} />

                <div className={classes.positionContent}>
                  <div className={classes.positionHeader}>
                    <Typography className={classes.positionLabel}>{t("devices.tracking.config.status.operational_map.position.coordinates", "Coordenadas")}</Typography>

                    <Button
                      size="small"
                      color="primary"
                      className={classes.copyButton}
                      startIcon={<FileCopyIcon fontSize="small" />}
                      disabled={coordinatesText === "-"}
                      onClick={() => handleCopy(coordinatesText, t("devices.tracking.config.status.operational_map.position.coordinates_copied", "Coordenadas copiadas"))}
                    >
                      {t("devices.tracking.config.status.operational_map.position.copy", "Copiar")}
                    </Button>
                  </div>

                  <Typography className={classes.coordinatesValue}>{coordinatesText}</Typography>
                </div>
              </div>

              <div className={classes.positionItem}>
                <AccessTimeIcon className={classes.positionIcon} />
                <div>
                  <Typography className={classes.positionLabel}>{t("devices.tracking.config.status.operational_map.position.datetime", "Último registro R100")}</Typography>

                  <Typography className={classes.positionValue}>{getOperationalPositionDate(operationalPosition, effectiveTrackerOperationalGis)}</Typography>
                </div>
              </div>
            </div>

            <div className={classes.contextCard}>
              <div className={classes.contextItem}>
                <div className={`${classes.contextIcon} ${classes.contextR100Icon}`}>
                  <FavoriteBorderIcon />
                </div>

                <div>
                  <Typography className={classes.contextTitle}>{t("devices.tracking.config.status.operational_map.context.r100", "R100 (Evento)")}</Typography>
                  <Typography className={classes.contextValue}>
                    {t("devices.tracking.config.status.operational_map.context.serial", "SN")}: {effectiveDevice?.serial || "-"}
                  </Typography>
                  <Typography className={classes.contextValue}>{effectiveDevice?.name || "-"}</Typography>
                </div>
              </div>

              <div className={classes.contextItem}>
                <div className={`${classes.contextIcon} ${classes.contextBusIcon}`} style={{ color: busStatusInfo?.iconColor || "#009fe3" }}>
                  <DirectionsBusIcon />
                </div>

                <div>
                  <Typography className={classes.contextTitle}>{t("devices.tracking.config.status.operational_map.context.bus", "Bus asociado")}</Typography>
                  <Typography className={classes.contextValue}>
                    {t("devices.tracking.config.status.operational_map.context.bus_number", "Bus")}: {associatedBusNumber}
                  </Typography>
                  <Typography className={classes.contextValue}>
                    {t("devices.tracking.config.status.operational_map.context.license_plate", "Matrícula")}: {associatedBusLicensePlate}
                  </Typography>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={classes.header}>
              <div className={classes.headerIcon}>
                <LocationOnIcon />
              </div>

              <div>
                <Typography className={classes.title}>{t(titleKey)}</Typography>
                <Typography className={classes.subtitle}>{t(descriptionKey)}</Typography>
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={onClose} color="primary">
          {t("common.close")}
        </Button>
      </DialogActions>
      <Snackbar open={Boolean(copyMessage)} autoHideDuration={2200} onClose={() => setCopyMessage("")} message={copyMessage} />
    </Dialog>
  );
};

export default OperationalEventGisOverlay;
